import { useState, useCallback, useMemo, useEffect } from 'react';
import { WizardContainer } from '../../../components/wizard/Wizard';
import { InspirationAndWillingnessStep } from './InspirationAndWillingnessStep';
import { PracticeStep } from './PracticeStep';
import { CharacterAndHabitsStep } from './CharacterAndHabitsStep';
import { AvailabilityAfterProgramStep } from './AvailabilityAfterProgramStep';
import { SupportStep } from './SupportStep';
import { UnderstandingOfHeartfulnessStep } from './UnderstandingOfHeartfulnessStep';
import { TermsAndConditionStepContainer } from './TermsAndConditionStep';
import { WizardProvider } from '../../../components/wizard/useWizard';
import { NewApplicationProvider, useNewApplication } from './useNewApplication';
import { BasicEligibilityStep } from './BasicEligibilityStep';
import { CategoryAnswerType } from './CategoryAnswerType';
import { useTranslation } from 'react-i18next';
import { CardIndex } from '../../../shared/Constants';
import { DietaryHabitsAndHealthStep } from './DietaryHabitsAndHealthStep';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import { DraftState } from '../../../shared/VolunteerApplicationServiceTypes';
import { HeartfulnessProfileStepContainer } from './HeartfulnessProfileStep';
import { ApplicantAdditionalDetailsStep } from './ApplicantAdditionalDetailsStep';
import { PreceptorContactDetailsStep } from './PreceptorContactDetailsStep';
import { TrainingCourseDetailsStep } from './TrainingCourseDetailsStep';
import { FamilyMemberDetailsStep } from './FamilyMemberDetailsStep';
import { OtherReferenceDetailsStep } from './OtherReferenceDetailsStep';
import { AboutTheProcessContainer } from './AboutTheProcessStep';
import { Button, Modal, Typography } from 'antd';
import Icon from '../../../components/ui-components/Icon';
import ContactInfo from '../../../assets/images/contact-info.svg';
import { myHFNProfileURL } from '../../../service/EnvService';
import { EmailVerificationModal } from './EmailVerificationModal';

const { Link } = Typography;

export const NewApplicationWizard = () => {
	const { t } = useTranslation();
	const {
		mySrcmProfile,
		onSubmitClick,
		basicEligibilityCategoryAnswers,
		isEmailVerified,
		onSaveandNextClick,
		applicationDetails,
	} = useNewApplication();

	const { infoPopUpAction } = useInfoPopup();
	const [modalKey, setModalKey] = useState(0);
	const [openEmailVerificationModal, setOpenEmailVerificationModal] =
		useState(false);
	const [resolveFunction, setResolveFunction] = useState<
		((value: boolean) => void) | null
	>(null);
	const [currentWizard, setCurrentWizard] = useState<number>(0);
	const [isWizardSubmitted, setIsWizardSubmitted] = useState(false);
	const [isWizardPrevioused, setisWizardPrevioused] = useState(false);
	const [stepDisable, setStepsDisable] = useState(false);
	const [flag, setFlag] = useState(0);

	const wizards = useMemo(
		() => [
			{
				steps: [
					<HeartfulnessProfileStepContainer key={1} />,
					<ApplicantAdditionalDetailsStep key={2} />,
					<TrainingCourseDetailsStep key={3} />,
					<PreceptorContactDetailsStep key={4} />,
					<FamilyMemberDetailsStep key={5} />,
					<OtherReferenceDetailsStep key={6} />,
					<AboutTheProcessContainer key={7} />,
				],
				descriptions: [
					{
						title: t('heartfulnessProfile'),
						description: t('detailsWillBeFetchedFromHeartfulnessProfile') ?? '',
					},
					{
						title: t('additionalDetails'),
						description: t('provideTheFollowingDetailsToHelp') ?? '',
					},
					{
						title: t('trainingCourse'),
						description:
							t('thisProcessTakes3To4MonthsRequiring5HoursOfEffortWeekly') ??
							'',
					},
					{
						title: t('preceptorContact'),
						description: t('detailsWillBeFetchedFromHeartfulnessProfile') ?? '',
					},
					{ title: t('familyMemberContact'), description: '' },
					{
						title: t('otherReference'),
						description: t('provideContactOfAnyOtherPerson') ?? '',
					},
					{ title: t('aboutTheProcess') },
				],
			},
			{
				steps: [
					<BasicEligibilityStep key={8} />,
					<InspirationAndWillingnessStep key={9} />,
					<PracticeStep key={10} />,
					<CharacterAndHabitsStep key={11} />,
					<AvailabilityAfterProgramStep key={12} />,
					<SupportStep key={13} />,
					<UnderstandingOfHeartfulnessStep key={14} />,
					<DietaryHabitsAndHealthStep key={15} />,
					<TermsAndConditionStepContainer key={16} />,
				],
				descriptions: [
					{
						title: t('essentials'),
						description: t('thisIsMandatoryCriteria') ?? '',
					},
					{
						title: t('inspirationAndWillingness'),
						description: t('primaryQualificationForATrainer') ?? '',
					},
					{
						title: t('practice'),
						description: t('trainerNeedsToBeIntensePractitioner') ?? '',
					},
					{
						title: t('characterAndHabits'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('availabilityAfterProgram'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('familySupport'),
						description: t('exploreIfConditionsAroundAreConducive') ?? '',
					},
					{
						title: t('understandingHeartfulness'),
						description: t('abilityToExplainHeartfulness') ?? '',
					},
					{
						title: t('dietaryHabitsAndHealth'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{ title: t('termsAndConditions') },
				],
			},
		],
		[t]
	);

	const applicationId = applicationDetails?.applicationId as number;
	const draftStateValues = Object.values(DraftState) as string[];

	const getDraftStateIndex = useCallback((state: string): number => {
		const DraftArrayValues = Object.values(DraftState);
		return DraftArrayValues.indexOf(state as DraftState);
	}, []);

	const getDraftIndex = useCallback(() => {
		return applicationDetails?.draftState
			? getDraftStateIndex(applicationDetails.draftState)
			: 0;
	}, [applicationDetails, getDraftStateIndex]);

	const getCompletedDraftIndex = useCallback(() => {
		return applicationDetails?.completedDraftState
			? getDraftStateIndex(applicationDetails?.completedDraftState)
			: 0;
	}, [applicationDetails, getDraftStateIndex]);

	const completedDraftIndex = useCallback(() => {
		const draftIndex = getDraftIndex();
		const completedIndex = getCompletedDraftIndex();
		return Math.max(draftIndex, completedIndex);
	}, [getCompletedDraftIndex, getDraftIndex]);

	const [stepStates, setStepStates] = useState<number[]>(() => {
		const draftIndex = getDraftIndex();
		return new Array(wizards.length)
			.fill(0)
			.map((_, index) =>
				index === currentWizard ? Math.max(0, draftIndex) : 0
			);
	});

	useEffect(() => {
		window.scroll(0, 0);
	}, []);

	useEffect(() => {
		const draftIndex = getDraftIndex();
		if (draftIndex > 6 && !flag && currentWizard === 0) {
			setCurrentWizard(1);
		}
	}, [getDraftIndex, flag, currentWizard]);

	useEffect(() => {
		const draftIndex = getDraftIndex();
		setStepStates((prevStates) => {
			const updatedStates = [...prevStates];
			if (
				currentWizard === 0 &&
				!isWizardPrevioused &&
				draftIndex <= 6 &&
				draftIndex != 0
			) {
				updatedStates[currentWizard] = draftIndex;
			} else if (currentWizard === 1 && !isWizardSubmitted && draftIndex > 6) {
				updatedStates[currentWizard] = draftIndex - 7;
			}

			return updatedStates;
		});
	}, [currentWizard, getDraftIndex, isWizardSubmitted, isWizardPrevioused]);

	const hasAnswerOtherThanYes = useCallback(
		(categoryAnswers: CategoryAnswerType) => {
			return Object.values(categoryAnswers).some(
				(item: { selectedOption: number }) => item.selectedOption !== 1
			);
		},
		[]
	);

	const closeEmailVerificationModal = useCallback(() => {
		setOpenEmailVerificationModal(false);
		if (resolveFunction) {
			resolveFunction(false);
		}
	}, [resolveFunction]);

	const VerifyContactContent = useCallback(() => {
		return (
			<div className="flex flex-col font-sans font-normal text-sm">
				<div className="flex flex-col items-center">
					<div className="w-24 h-20 my-4">
						<Icon src={ContactInfo} className="w-full h-full" />
					</div>
					<div>{t('areYouSureToProceed')}</div>
					<div>
						{t('ourTeamWillReachYou')} <b>{mySrcmProfile?.mobile}</b>
					</div>
				</div>
				<div className="mt-4">
					{t('incorrectPhoneNumber')}{' '}
					<Link
						href={myHFNProfileURL}
						target="_blank"
						className="px-1 color-black text-sm font-normal font-sans text-primary-blue"
						underline
					>
						{t('editYourNumber')}
					</Link>
				</div>
			</div>
		);
	}, [mySrcmProfile?.mobile, t]);

	const VerifyContactFooter = useCallback(
		(resolve: (value: boolean | PromiseLike<boolean>) => void) => {
			return (
				<div className="flex w-full gap-4 mt-4">
					<Button
						type="default"
						className="flex-1 h-9 px-4 py-2 font-sans font-bold text-sm border border-primary-blue rounded-lg text-primary-blue"
						onClick={() => Modal.destroyAll()}
					>
						{t('deny')}
					</Button>
					<Button
						type="default"
						className="flex-1 h-9 px-4 py-2 font-sans font-bold text-sm bg-primary-blue text-white rounded-lg"
						onClick={() => {
							Modal.destroyAll();
							setModalKey((prevKey) => prevKey + 1);
							setOpenEmailVerificationModal(true);
							setResolveFunction(() => resolve);
						}}
					>
						{t('agree')}
					</Button>
				</div>
			);
		},
		[t]
	);

	const showModal = useCallback(
		async (cardIndex: number): Promise<boolean> => {
			setStepsDisable(false);
			const nextDraftState = draftStateValues[cardIndex + 1] as DraftState;
			const currentDraftIndex = Math.max(
				getDraftStateIndex(draftStateValues[cardIndex] as DraftState),
				getCompletedDraftIndex()
			);
			const currentDraftState = draftStateValues[
				currentDraftIndex
			] as DraftState;

			if (
				cardIndex === CardIndex.HEARTFULNESS_PROFILE &&
				!isEmailVerified &&
				!applicationDetails?.draftState
			) {
				return new Promise((resolve) => {
					Modal.confirm({
						className: 'max-w-md',
						centered: true,
						okText: t('agree'),
						cancelText: t('deny'),
						icon: null,
						title: (
							<div className="text-center font-sans font-bold text-lg">
								{t('verifyContactInfo')}
							</div>
						),
						content: VerifyContactContent(),
						footer: VerifyContactFooter(resolve),
					});
				});
			} else if (cardIndex === CardIndex.BASIC_ELIGIBILITY) {
				const blockTheUserFromProceddingAhead = hasAnswerOtherThanYes(
					basicEligibilityCategoryAnswers
				);
				if (blockTheUserFromProceddingAhead) {
					setStepsDisable(true);
					return new Promise(() => {
						infoPopUpAction(
							'basicEligibilityCriteriaNotMet',
							'notMeetingBasicEligibilityCriteria',
							() => {}
						);
					});
				} else {
					await onSaveandNextClick(
						DraftState.INSPIRATION_AND_WILLINGNESS,
						currentDraftState,
						applicationId
					);
				}
			} else {
				if (nextDraftState) {
					await onSaveandNextClick(
						nextDraftState,
						currentDraftState,
						applicationId
					);
				}
			}
			return Promise.resolve(true);
		},
		[
			draftStateValues,
			getDraftStateIndex,
			getCompletedDraftIndex,
			isEmailVerified,
			applicationDetails?.draftState,
			t,
			VerifyContactContent,
			VerifyContactFooter,
			hasAnswerOtherThanYes,
			basicEligibilityCategoryAnswers,
			infoPopUpAction,
			onSaveandNextClick,
			applicationId,
		]
	);

	const onSubmitWizard = useCallback(async () => {
		await onSaveandNextClick(
			DraftState.ESSENTIALS,
			DraftState.ABOUT_THE_PROCESS,
			applicationId
		);

		setFlag(1);
		setIsWizardSubmitted(true);
		setCurrentWizard((prevWizard) => {
			const newWizard = prevWizard + 1;
			window.scrollTo(0, 0);
			setStepStates((prevStates) => {
				const updatedStates = [...prevStates];

				if (updatedStates[newWizard] === undefined || isWizardSubmitted) {
					updatedStates[newWizard] = 0;
				} else {
					updatedStates[newWizard] = 0;
				}

				return updatedStates;
			});

			return newWizard;
		});
	}, [onSaveandNextClick, applicationId, isWizardSubmitted]);

	const onPreviousWizard = useCallback(() => {
		setFlag(1);
		setisWizardPrevioused(true);
		setStepsDisable(false);
		setCurrentWizard((prevWizard) => {
			const newWizard = prevWizard - 1;
			window.scrollTo(0, 0);
			setStepStates((prevStates) => {
				const updatedStates = [...prevStates];
				updatedStates[newWizard] = wizards[newWizard].steps.length - 1;
				return updatedStates;
			});
			return newWizard;
		});
	}, [wizards]);

	const steps = wizards[currentWizard].steps;
	const descriptions = wizards[currentWizard].descriptions;
	const stepOffset = 0;
	const totalStepsInPreviousWizard =
		currentWizard > 0 ? wizards[currentWizard - 1].steps.length : 0;

	return (
		<div className="flex flex-col h-full min-h-0 p-[8px]">
			<WizardContainer
				onSubmitClick={() => {
					if (currentWizard === wizards.length - 1) {
						onSubmitClick();
					} else {
						void onSubmitWizard();
					}
				}}
				onNextClick={showModal}
				steps={steps}
				stepDescription={descriptions}
				stepOffset={stepOffset}
				initialStepState={stepStates[currentWizard] || 0}
				onStepStateChange={(newState: number) => {
					setStepStates((prev) => {
						const updated = [...prev];
						updated[currentWizard] = newState;
						return updated;
					});
				}}
				currentWizard={currentWizard}
				onPreviousClick={onPreviousWizard}
				totalStepsInPreviousWizard={totalStepsInPreviousWizard}
				draftIndex={getDraftIndex()}
				stepDisable={stepDisable}
				completedDraftIndex={completedDraftIndex()}
			/>
			<EmailVerificationModal
				key={modalKey}
				openModal={openEmailVerificationModal}
				onCancel={closeEmailVerificationModal}
				email={mySrcmProfile.email}
				mobile={mySrcmProfile?.mobile}
				onEmailVerificationComplete={resolveFunction}
			/>
		</div>
	);
};

export const NewApplicationWizardContainer = () => {
	return (
		<NewApplicationProvider>
			<WizardProvider>
				<NewApplicationWizard />
			</WizardProvider>
		</NewApplicationProvider>
	);
};
